import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Globe2, ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const CITIES = [
  { name: "Piatra Neamț", slug: "servicii-it-piatra-neamt-neamt" },
  { name: "Roman", slug: "servicii-it-roman" },
  { name: "Târgu Neamț", slug: "servicii-it-targu-neamt" },
  { name: "Bacău", slug: "servicii-it-bacau" },
  { name: "Suceava", slug: "servicii-it-suceava" },
  { name: "Gura Humorului", slug: "servicii-it-gura-humorului" },
  { name: "Iași", slug: "servicii-it-iasi" },
  { name: "Botoșani", slug: "servicii-it-botosani" },
  { name: "Vaslui", slug: "servicii-it-vaslui" },
];

const C = {
  ro: {
    label: "Acoperire",
    h1: "Zone",
    hkw: "acoperite.",
    sub: "Avem sediul în Piatra Neamț și lucrăm în tot arealul Moldovei — plus suport remote la nivel național, oriunde ai sedii.",
    national: "Suport remote — la nivel național",
  },
  en: {
    label: "Coverage",
    h1: "Areas",
    hkw: "we cover.",
    sub: "We're based in Piatra Neamț and work across the whole Moldova region — plus remote support nationwide, wherever your sites are.",
    national: "Remote support — nationwide",
  },
};

export const Coverage = () => {
  const { lang } = useI18n();
  const d = C[lang === "en" ? "en" : "ro"];

  return (
    <section
      id="acoperire"
      data-testid="coverage-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink-800"
    >
      <div className="pointer-events-none absolute -right-32 top-0 h-[24rem] w-[24rem] rounded-full bg-cyan-neon/10 blur-[150px]" />

      <div className="relative mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ ACOPERIRE ]">{d.label}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
              {d.h1} <span className="text-aurora text-aurora-glow">{d.hkw}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-white/60">{d.sub}</p>
          </Reveal>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="coverage-cities">
          {CITIES.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, ease: EASE, delay: (i % 3) * 0.06 }}
            >
              <Link
                to={`/blog/${c.slug}`}
                data-testid={`coverage-link-${c.slug}`}
                className="group flex items-center justify-between rounded-xl border border-white/10 bg-ink-900 px-5 py-4 transition-colors duration-300 hover:border-cyan-neon/50 hover:bg-ink-700"
              >
                <span className="flex items-center gap-3">
                  <MapPin size={17} className="text-cyan-neon" />
                  <span className="font-display text-base font-semibold text-white">{c.name}</span>
                </span>
                <ArrowUpRight size={16} className="text-white/25 transition-colors duration-300 group-hover:text-cyan-neon" />
              </Link>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-cyan-neon/25 bg-cyan-neon/[0.06] px-5 py-2.5 font-mono text-xs uppercase tracking-widest text-cyan-neon">
            <Globe2 size={15} /> {d.national}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
